import type { Blocker, Transition } from 'history';
import React, {
	useCallback,
	useContext,
	useEffect,
	useRef,
	useState
} from 'react';
import {
	Navigator,
	UNSAFE_NavigationContext,
	useNavigate
} from 'react-router-dom';
import ApprovalDialog from '../ApprovalDialog/ApprovalDialog';

type BlockerNavigator = Navigator & {
	location: Location;
	block(blocker: Blocker): () => void;
};

interface INavigationApprovalDialogProps {
	hasUnsavedChanges: boolean;
}

const NavigationApprovalDialog = ({
	hasUnsavedChanges
}: INavigationApprovalDialogProps) => {
	const { navigator } = useContext(UNSAFE_NavigationContext);

	const [displayAlert, setDisplayAlert] = useState(false);

	const unblockRef = useRef(() => {
		[].forEach((e) => e);
	});

	const navigate = useNavigate();
	const [navigationTransition, setNavigationTransition] =
		useState<Transition>();

	useEffect(() => {
		if (!hasUnsavedChanges) {
			unblockRef?.current?.();
			return () => {};
		}

		const { block } = navigator as BlockerNavigator;
		unblockRef.current = block((transition) => {
			setNavigationTransition(transition);
			setDisplayAlert(true);
		});
		return () => unblockRef?.current?.();
	}, [navigator, navigate, hasUnsavedChanges]);

	const leavePage = useCallback(() => {
		setDisplayAlert(false);
		unblockRef.current();
		// We use this to make it work with navigations without react-router (ie. Browser's go back button)
		if (navigationTransition?.action.toLowerCase() === 'pop') {
			navigate(navigationTransition.location.pathname);
		} else {
			// We use this to make it work with navigations via react-router (ie. navLink)
			navigationTransition?.retry();
		}
	}, [setDisplayAlert, unblockRef, navigationTransition, navigate]);

	const stayOnPage = useCallback(() => {
		setDisplayAlert(false);
	}, [setDisplayAlert]);

	return displayAlert ? (
		<ApprovalDialog
			onCancel={stayOnPage}
			onApprove={leavePage}
			title={intl.tr('discard.changes.dialog.title')}
			question={intl.tr('discard.changes.dialog.question')}
			approveText={intl.tr('discard.changes.dialog.approve')}
			cancelText={intl.tr('button.keepEdit')}
		/>
	) : null;
};

export default NavigationApprovalDialog;
